import React, { useState } from 'react';
import { OrdemDeServicoCompleta } from '../../types/ordemServico';
import { Printer, X, FileText, Calendar, Download, Loader2 } from 'lucide-react';
import { generateOsPdf } from '../../services/osPdfGenerator';

interface OsHeaderSectionProps {
  os: OrdemDeServicoCompleta;
  onClose?: () => void;
  onPrint?: () => void;
}

export const OsHeaderSection: React.FC<OsHeaderSectionProps> = ({ os, onClose, onPrint }) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setIsGeneratingPdf(true);
      await generateOsPdf(os);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const statusColor = os.status?.cor || '#64748b';
  const statusName = os.status?.status || `Status #${os.status_id || 'N/A'}`;

  return (
    <div className="sticky top-0 bg-white z-20 -mx-4 -mt-4 sm:-mx-6 sm:-mt-6 px-4 pt-4 sm:px-6 sm:pt-5 pb-4 border-b border-slate-200 shadow-xs">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 text-red-600 rounded-xl">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                Visualizar O.S #{os.id}
                <span
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-white shadow-xs"
                  style={{ backgroundColor: statusColor }}
                >
                  {statusName}
                </span>
              </h2>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  Criado: <strong className="text-slate-700">{formatDate(os.created_at)}</strong>
                </span>
                {os.updated_at && (
                  <span className="hidden sm:inline">
                    | Atualizado: <strong className="text-slate-700">{formatDate(os.updated_at)}</strong>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 print:hidden self-end sm:self-center">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50"
          >
            {isGeneratingPdf ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Gerar PDF
          </button>

          {onPrint && (
            <button
              type="button"
              onClick={onPrint}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </button>
          )}

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
